import { useState, useMemo } from 'react';
import {
  Box,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Grid,
  Chip,
  Slider,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import NatureIcon from '@mui/icons-material/Nature';
import BoltIcon from '@mui/icons-material/Bolt';
import Co2Icon from '@mui/icons-material/Co2';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { getChartColors, formatCurrency } from '../utils/chartConfig';
import MetricExplanationModal from './MetricExplanationModal';
import SavingsProjectionChart from './SavingsProjectionChart';
import WaterfallChart from './WaterfallChart';
import YearlyProjectionTable from './YearlyProjectionTable';
import BeforeAfterChart from './BeforeAfterChart';
import EquipmentTypeBreakdown from './EquipmentTypeBreakdown';
import PaybackHeatmap from './PaybackHeatmap';
import CFOCapitalAnalysis from './CFOCapitalAnalysis';
import EquipmentComparisonChart from './EquipmentComparisonChart';
import PortfolioSummary from './PortfolioSummary';
import PaymentComparison from './PaymentComparison';
import CFORecommendationSection from './CFORecommendationSection';
import RiskScaleSection from './RiskScaleSection';
import CapitalAccountingSection from './CapitalAccountingSection';
import {
  calculateProjectionsWithEscalation,
  generatePortfolioBeforeAfterData,
  calculatePortfolioElectricityRate,
  DEFAULT_ESCALATION_RATE,
} from '../services/easCalculations';

/**
 * FullComparisonModal - Complete side-by-side payment comparison
 *
 * Shows ALL metrics for both Upfront and 3-Year payment options in parallel columns.
 * Includes all sections from the main page: Financial Impact Analysis, ESG, Charts.
 */
export default function FullComparisonModal({
  open,
  onClose,
  portfolioData,
  paymentMetrics,
  activeMode,
  cfoMetrics,
  companyConfig,
  roiResults,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const colors = getChartColors(theme.palette.mode);
  const [openMetric, setOpenMetric] = useState(null);
  const [escalationRate, setEscalationRate] = useState(DEFAULT_ESCALATION_RATE * 100);

  // Calculate escalation projections
  const escalationData = useMemo(() => {
    if (!portfolioData?.summary) return null;

    const { total_annual_value, total_treatment_cost } = portfolioData.summary;
    const rate = escalationRate / 100;

    return calculateProjectionsWithEscalation(
      total_annual_value,
      10, // 10-year projection
      rate,
      total_treatment_cost
    );
  }, [portfolioData, escalationRate]);

  // Calculate before/after chart data for full portfolio
  const beforeAfterData = useMemo(() => {
    if (!roiResults?.individual || roiResults.individual.length === 0) return null;

    const validEquipment = roiResults.individual.filter(eq => eq.valid);
    if (validEquipment.length === 0) return null;

    return generatePortfolioBeforeAfterData({
      equipmentList: validEquipment,
      electricityRate: calculatePortfolioElectricityRate(validEquipment),
      projectionYears: 10,
      escalationRate: escalationRate / 100,
    });
  }, [roiResults, escalationRate]);

  // Handle print - opens the print view in a new tab
  const handlePrint = () => {
    const printData = {
      roiResults,
      cfoMetrics,
      paymentMetrics,
      companyConfig,
      cfoAnalysis: {
        npv: {
          upfront: cfoMetrics?.tier4_capital?.npv,
          threeYear: cfoMetrics?.tier4_capital?.npv, // Same NPV for both
          discountRatePct: (companyConfig?.wacc || 0.08) * 100,
          advantage: 0,
          advantagePct: 0,
        },
        capitalPreservation: {
          year1Retained: paymentMetrics?.threeYear?.totalCost ? paymentMetrics.threeYear.totalCost * (2/3) : 0,
          year2Retained: paymentMetrics?.threeYear?.totalCost ? paymentMetrics.threeYear.totalCost * (1/3) : 0,
          averageCapitalAvailable: paymentMetrics?.threeYear?.totalCost ? paymentMetrics.threeYear.totalCost * 0.5 : 0,
        },
        accounting: {
          upfront: { type: 'CapEx', treatment: 'Capitalized asset', depreciation: 'Over useful life', taxImpact: 'Spread over depreciation', budgetApproval: 'CapEx approval required' },
          threeYear: { type: 'OpEx', treatment: 'Operating expense', depreciation: 'N/A - expensed', taxImpact: 'Full deduction in year', budgetApproval: 'Simpler OpEx budget' },
        },
      },
    };
    sessionStorage.setItem('easPrintData', JSON.stringify(printData));
    window.open('/eas-print', '_blank');
  };

  if (!portfolioData || !paymentMetrics) return null;

  const { summary, value_breakdown, environmental, projections, value_composition } = portfolioData;
  const { upfront, threeYear } = paymentMetrics;

  // Value composition percentages
  const energySavingsPct = value_composition?.energy_savings_pct || 25;
  const avoidedCostsPct = value_composition?.avoided_costs_pct || 75;

  // Metric values for dynamic examples in explanation modal
  const metricValues = cfoMetrics ? {
    ebitda: cfoMetrics.tier1_ebitda?.contribution,
    margin: cfoMetrics.tier2_revenue?.profitMarginUsed || 0.05,
    revenueEquiv: cfoMetrics.tier2_revenue?.equivalentRevenue,
    enterpriseValue: cfoMetrics.tier3_valuation?.enterpriseValueIncrease || cfoMetrics.tier3_valuation?.marketCapIncrease || cfoMetrics.tier3_valuation?.valueIncrease,
    multiple: cfoMetrics.tier3_valuation?.multipleUsed || 8,
    npv: cfoMetrics.tier4_capital?.npv,
    irr: cfoMetrics.tier4_capital?.irr,
    wacc: companyConfig?.wacc || 0.10,
    fiveYearRoi: summary?.five_year_roi,
  } : {};

  // Format helpers
  const formatNumber = (value, decimals = 0) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  };

  // Value components with colors
  // Only show mission-critical if it's material (> $500/year)
  const missionCriticalValue = value_breakdown?.mission_critical_protection || 0;
  const showMissionCritical = missionCriticalValue > 500;

  const valueComponents = [
    { key: 'energy_savings', label: 'Energy Savings', color: colors.success },
    { key: 'avoided_degradation', label: 'Avoided Degradation', color: colors.teal },
    { key: 'avoided_demand', label: 'Avoided Demand', color: colors.blue },
    { key: 'avoided_runtime', label: 'Avoided Runtime', color: colors.orangeLight },
    { key: 'life_extension', label: 'Life Extension', color: colors.primary },
    ...(showMissionCritical ? [{ key: 'mission_critical_protection', label: 'Mission-Critical', color: '#9C27B0' }] : []),
  ];

  // Helper: Section Title (spans full width)
  const SectionTitle = ({ children, color }) => (
    <Typography
      variant="caption"
      sx={{
        color: color || 'text.secondary',
        fontWeight: 500,
        fontSize: '0.65rem',
        display: 'block',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        mb: 0.5,
      }}
    >
      {children}
    </Typography>
  );

  // Helper: Column Header
  const ColumnHeader = ({ mode, isActive }) => {
    const isUpfront = mode === 'upfront';
    const accentColor = isUpfront ? colors.primary : colors.teal;

    return (
      <Box
        sx={{
          p: 1.5,
          pt: 2.5, // Extra top padding for the chip
          borderRadius: 1.5,
          background: (theme) => isActive
            ? `${accentColor}10`
            : (theme.palette.mode === 'dark' ? 'rgba(26, 31, 37, 0.6)' : 'rgba(248, 249, 250, 0.9)'),
          border: (theme) => isActive
            ? `1px solid ${accentColor}60`
            : `1px solid ${theme.palette.divider}`,
          position: 'relative',
          textAlign: 'center',
          transition: 'all 0.2s ease',
          overflow: 'visible',
        }}
      >
        {isActive && (
          <Chip
            label="Selected"
            size="small"
            icon={<CheckCircleIcon sx={{ fontSize: '0.85rem' }} />}
            sx={{
              position: 'absolute',
              top: -12,
              right: 12,
              background: accentColor,
              color: '#0A0A0A',
              fontWeight: 600,
              fontSize: '0.65rem',
              height: 20,
              '& .MuiChip-icon': { color: '#0A0A0A' },
              zIndex: 1,
            }}
          />
        )}
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            pb: 0.75,
            borderBottom: `2px solid ${accentColor}`,
            fontSize: '0.95rem',
          }}
        >
          {isUpfront ? 'Upfront Payment' : '3-Year Payment Program'}
        </Typography>
      </Box>
    );
  };

  // Helper: Hero Value section
  const HeroValue = ({ mode }) => {
    const isUpfront = mode === 'upfront';
    const accentColor = isUpfront ? colors.primary : colors.teal;

    return (
      <Box sx={{ textAlign: 'center', py: 1.5, px: 1 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem', letterSpacing: '0.08em' }}>
          TOTAL ANNUAL VALUE
        </Typography>
        <Typography
          sx={{
            fontSize: '2.25rem',
            fontWeight: 800,
            color: accentColor,
            fontFamily: '"JetBrains Mono", monospace',
            textShadow: `0 0 40px ${accentColor}40`,
            fontFeatureSettings: '"tnum"',
            lineHeight: 1.1,
          }}
        >
          {formatCurrency(summary.total_annual_value || summary.total_annual_savings)}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
          /year
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, mt: 1 }}>
          <Chip
            label={`Energy: ${energySavingsPct.toFixed(0)}%`}
            size="small"
            sx={{
              background: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(76, 175, 80, 0.12)'
                : 'rgba(46, 125, 50, 0.15)',
              color: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : '#2E7D32',
              fontWeight: 600,
              fontSize: '0.55rem',
              height: 18,
            }}
          />
          <Chip
            label={`Avoided: ${avoidedCostsPct.toFixed(0)}%`}
            size="small"
            sx={{
              background: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(78, 205, 196, 0.12)'
                : 'rgba(13, 115, 119, 0.15)',
              color: (theme) => theme.palette.mode === 'dark' ? colors.teal : '#0D7377',
              fontWeight: 600,
              fontSize: '0.55rem',
              height: 18,
            }}
          />
        </Box>
      </Box>
    );
  };

  // Helper: Stats Grid
  const StatsGrid = ({ mode }) => {
    const isUpfront = mode === 'upfront';

    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 1,
          p: 1.5,
          borderRadius: 1,
          background: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.05)',
        }}
      >
        <StatItem label="Equipment" value={summary.total_equipment} unit="units" />
        <StatItem label="Tonnage" value={formatNumber(summary.total_tonnage, 1)} unit="tons" />
        <StatItem
          label="Payback"
          value={isUpfront ? formatNumber(summary.weighted_average_payback_months, 1) : 'NONE'}
          unit={isUpfront ? 'mo' : ''}
          highlight={!isUpfront}
          highlightColor={colors.teal}
          metricKey={isUpfront ? 'payback_months' : 'no_payback_needed'}
          onInfoClick={setOpenMetric}
          subtext={isUpfront && summary.energy_only_payback_months
            ? `(vs ${formatNumber(summary.energy_only_payback_months, 1)}mo energy-only)`
            : null}
        />
        <StatItem label="5-Yr ROI" value={formatNumber(summary.five_year_roi, 0)} unit="%" />
      </Box>
    );
  };

  // Helper: Value Breakdown section
  const ValueBreakdownSection = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {valueComponents.map((item) => (
        <Box
          key={item.key}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 0.5,
            px: 0.75,
            borderRadius: 0.5,
            borderLeft: `2px solid ${item.color}`,
            background: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.04)',
            transition: 'all 0.15s ease',
            '&:hover': {
              background: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.08)',
              transform: 'translateX(2px)',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" sx={{ color: item.color, fontWeight: 600, fontSize: '0.6rem' }}>
              {item.label}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOpenMetric(item.key)}
              sx={{ p: 0.25, color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)', '&:hover': { color: item.color } }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 10 }} />
            </IconButton>
          </Box>
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 700,
              fontFamily: '"JetBrains Mono", monospace',
              fontFeatureSettings: '"tnum"',
              color: 'text.primary',
            }}
          >
            {formatCurrency(value_breakdown?.[item.key] || 0)}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  // Helper: Cash Flow section
  const CashFlowSection = ({ mode }) => {
    const isUpfront = mode === 'upfront';
    const metrics = isUpfront ? upfront : threeYear;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <CashFlowItem
          label={isUpfront ? 'Year 1 Outlay' : 'Year 1 Payment'}
          value={formatCurrency(isUpfront ? metrics.year1Outlay : metrics.annualPayment)}
          negative={isUpfront}
          subtext={!isUpfront ? `(1/3 of ${formatCurrency(metrics.totalCost)})` : null}
        />
        <CashFlowItem
          label="Year 1 Net"
          value={`${metrics.year1NetBenefit >= 0 ? '+' : ''}${formatCurrency(metrics.year1NetBenefit)}`}
          positive={metrics.year1NetBenefit > 0}
          negative={metrics.year1NetBenefit < 0}
          badge={metrics.year1NetBenefit > 0 ? 'Cash Positive' : null}
        />
        <CashFlowItem
          label="Monthly Cash Flow"
          value={`+${formatCurrency(metrics.monthlyCashFlow)}/mo`}
          positive
          subtext={!isUpfront ? 'After payment' : 'Full savings'}
        />
      </Box>
    );
  };

  // Helper: Environmental section
  const EnvironmentalSection = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
          Energy Saved
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: isDark ? colors.success : 'text.primary', fontFamily: '"JetBrains Mono", monospace', fontFeatureSettings: '"tnum"' }}>
          {formatNumber(summary.total_kwh_saved)} kWh/yr
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
          CO2 Avoided
        </Typography>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: isDark ? colors.success : 'text.primary', fontFamily: '"JetBrains Mono", monospace', fontFeatureSettings: '"tnum"' }}>
          {formatNumber(environmental?.co2_avoided_tonnes, 1)} tonnes
        </Typography>
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'right', fontSize: '0.6rem' }}>
        = {environmental?.vehicle_equivalent} cars removed
      </Typography>
    </Box>
  );

  // Helper: 5-Year Total section
  const FiveYearSection = ({ mode }) => {
    const isUpfront = mode === 'upfront';
    const metrics = isUpfront ? upfront : threeYear;
    const accentColor = isUpfront ? colors.primary : colors.teal;

    return (
      <Box
        sx={{
          p: 1.5,
          borderRadius: 1,
          background: `${accentColor}08`,
          borderLeft: `2px solid ${accentColor}60`,
          height: '100%',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
            Total Value
          </Typography>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: 'text.primary', fontFamily: '"JetBrains Mono", monospace', fontFeatureSettings: '"tnum"' }}>
            {formatCurrency(projections?.year_5_cumulative_value || projections?.year_5_cumulative)}
          </Typography>
        </Box>
        {projections?.year_5_cumulative && projections?.year_5_cumulative_value && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.55rem' }}>
              ({formatCurrency(projections.year_5_cumulative)} energy only)
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
            Net Profit
          </Typography>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: isDark ? colors.success : 'text.primary', fontFamily: '"JetBrains Mono", monospace', fontFeatureSettings: '"tnum"' }}>
            {formatCurrency(metrics.fiveYearNetProfit)}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            background: (theme) => theme.palette.mode === 'dark' ? '#0D1117' : '#FFFFFF',
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            color: (theme) => theme.palette.text.primary,
            py: 1.5,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Complete Payment Comparison
            </Typography>
            <Typography variant="caption" sx={{ color: colors.grey }}>
              Side-by-side analysis of all metrics
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              onClick={handlePrint}
              sx={{ color: colors.grey, '&:hover': { color: colors.white } }}
              title="Print Report"
            >
              <PrintIcon />
            </IconButton>
            <IconButton
              onClick={onClose}
              sx={{ color: colors.grey, '&:hover': { color: colors.white } }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 4, overflow: 'visible' }}>
          {/* ============================================= */}
          {/* MONEY FIRST - CFO Financial Impact Analysis   */}
          {/* ============================================= */}

          {/* Financial Impact Analysis Section - SIDE BY SIDE COMPARISON */}
          {cfoMetrics && (
            <Grid
              container
              spacing={2}
              sx={{
                mb: 3,
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '1px',
                  height: '100%',
                  background: (theme) => theme.palette.mode === 'dark' ? 'linear-gradient(180deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.15) 100%)' : 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.03) 50%, rgba(0,0,0,0.1) 100%)',
                  display: { xs: 'none', md: 'block' },
                },
              }}
            >
              {/* UPFRONT Column */}
              <Grid item xs={12} md={6} sx={{ pr: { md: 2 } }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    background: (theme) => activeMode === 'upfront'
                      ? `${colors.primary}08`
                      : theme.palette.mode === 'dark' ? 'rgba(26, 31, 37, 0.6)' : 'rgba(248, 249, 250, 0.9)',
                    border: (theme) => activeMode === 'upfront'
                      ? `1px solid ${colors.primary}40`
                      : `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <TrendingUpIcon sx={{ color: `${colors.primary}99`, fontSize: '1rem' }} />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: isDark ? colors.primary : 'text.primary', fontSize: '0.7rem', letterSpacing: '0.03em' }}>
                      UPFRONT - Financial Impact
                    </Typography>
                  </Box>

                  {/* Core Financial Metrics - Upfront */}
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={12}>
                      <MetricBox label="EBITDA Impact" value={formatCurrency(cfoMetrics.tier1_ebitda?.contribution || 0)} subtext="/year" color={colors.teal} onInfoClick={() => setOpenMetric('ebitda_impact')} />
                    </Grid>
                    <Grid item xs={6}>
                      <MetricBox label="Revenue Equiv" value={formatCurrency(cfoMetrics.tier2_revenue?.equivalentRevenue || 0)} subtext={`at ${((cfoMetrics.tier2_revenue?.profitMarginUsed || 0.05) * 100).toFixed(0)}% margin`} color={colors.blue} onInfoClick={() => setOpenMetric('revenue_equivalent')} />
                    </Grid>
                    <Grid item xs={6}>
                      <MetricBox label="Enterprise Value" value={formatCurrency(cfoMetrics.tier3_valuation?.enterpriseValueIncrease || cfoMetrics.tier3_valuation?.marketCapIncrease || cfoMetrics.tier3_valuation?.valueIncrease || 0)} subtext={`at ${cfoMetrics.tier3_valuation?.multipleUsed || 8}x EBITDA`} color={colors.primary} onInfoClick={() => setOpenMetric('enterprise_value')} />
                    </Grid>
                  </Grid>

                  {/* Capital Efficiency - Upfront */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <AccountBalanceIcon sx={{ color: `${colors.orange}99`, fontSize: '0.9rem' }} />
                    <Typography variant="caption" sx={{ fontWeight: 500, color: `${colors.orange}99`, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.6rem' }}>
                      Capital Efficiency
                    </Typography>
                  </Box>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <MetricBox label="Payback Period" value={`${formatNumber(summary.weighted_average_payback_months, 1)} mo`} subtext={`NPV: ${formatCurrency(cfoMetrics.tier4_capital?.npv || 0)}`} color={colors.white} onInfoClick={() => setOpenMetric('npv')} />
                    </Grid>
                    <Grid item xs={6}>
                      <MetricBox label="IRR" value={`${formatNumber((cfoMetrics.tier4_capital?.irr || 0) * 100, 0)}%`} color={colors.teal} onInfoClick={() => setOpenMetric('irr')} />
                    </Grid>
                    <Grid item xs={12}>
                      <MetricBox label="5-Year ROI" value={`${formatNumber(summary.five_year_roi || 0, 0)}%`} subtext="total return" color={colors.primary} onInfoClick={() => setOpenMetric('five_year_roi')} />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* 3-YEAR Column */}
              <Grid item xs={12} md={6} sx={{ pl: { md: 2 } }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 1.5,
                    background: (theme) => activeMode === 'threeYear'
                      ? `${colors.teal}08`
                      : theme.palette.mode === 'dark' ? 'rgba(26, 31, 37, 0.6)' : 'rgba(248, 249, 250, 0.9)',
                    border: (theme) => activeMode === 'threeYear'
                      ? `1px solid ${colors.teal}40`
                      : `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <TrendingUpIcon sx={{ color: `${colors.teal}99`, fontSize: '1rem' }} />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: isDark ? colors.teal : 'text.primary', fontSize: '0.7rem', letterSpacing: '0.03em' }}>
                      3-YEAR PLAN - Financial Impact
                    </Typography>
                  </Box>

                  {/* Core Financial Metrics - 3-Year (same values) */}
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={12}>
                      <MetricBox label="EBITDA Impact" value={formatCurrency(cfoMetrics.tier1_ebitda?.contribution || 0)} subtext="/year" color={colors.teal} onInfoClick={() => setOpenMetric('ebitda_impact')} />
                    </Grid>
                    <Grid item xs={6}>
                      <MetricBox label="Revenue Equiv" value={formatCurrency(cfoMetrics.tier2_revenue?.equivalentRevenue || 0)} subtext={`at ${((cfoMetrics.tier2_revenue?.profitMarginUsed || 0.05) * 100).toFixed(0)}% margin`} color={colors.blue} onInfoClick={() => setOpenMetric('revenue_equivalent')} />
                    </Grid>
                    <Grid item xs={6}>
                      <MetricBox label="Enterprise Value" value={formatCurrency(cfoMetrics.tier3_valuation?.enterpriseValueIncrease || cfoMetrics.tier3_valuation?.marketCapIncrease || cfoMetrics.tier3_valuation?.valueIncrease || 0)} subtext={`at ${cfoMetrics.tier3_valuation?.multipleUsed || 8}x EBITDA`} color={colors.primary} onInfoClick={() => setOpenMetric('enterprise_value')} />
                    </Grid>
                  </Grid>

                  {/* Capital Efficiency - 3-Year (different payback!) */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <AccountBalanceIcon sx={{ color: `${colors.teal}99`, fontSize: '0.9rem' }} />
                    <Typography variant="caption" sx={{ fontWeight: 500, color: `${colors.teal}99`, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.6rem' }}>
                      Capital Efficiency
                    </Typography>
                  </Box>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <MetricBox
                        label="Payback Period"
                        value="NONE"
                        subtext="Cash flow positive Day 1"
                        color={colors.teal}
                        onInfoClick={() => setOpenMetric('no_payback_needed')}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <MetricBox label="IRR" value="INFINITE" subtext="Always profitable" color={colors.teal} onInfoClick={() => setOpenMetric('irr')} />
                    </Grid>
                    <Grid item xs={12}>
                      <MetricBox label="5-Year ROI" value={`${formatNumber(summary.five_year_roi || 0, 0)}%`} subtext="total return" color={colors.primary} onInfoClick={() => setOpenMetric('five_year_roi')} />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          )}

          {/* Waterfall Chart - Financial Impact Visualization */}
          {cfoMetrics && (
            <Box sx={{ mb: 3 }}>
              <WaterfallChart cfoMetrics={cfoMetrics} />
            </Box>
          )}

          {/* CFO Capital Allocation Analysis - Strategic Framing */}
          {roiResults && cfoMetrics && companyConfig && (
            <Box sx={{ mb: 3 }}>
              <CFOCapitalAnalysis
                roiResults={roiResults}
                cfoMetrics={cfoMetrics}
                companyConfig={companyConfig}
                paymentMode={activeMode}
              />
            </Box>
          )}

          {/* Payment Scenario Comparison Cards */}
          {paymentMetrics && (
            <Box sx={{ mb: 3 }}>
              <PaymentComparison
                upfront={paymentMetrics.upfront}
                threeYear={paymentMetrics.threeYear}
                activeMode={activeMode}
              />
            </Box>
          )}

          {/* Row-Based Comparison - Aligned Section Titles */}
          <Grid
            container
            spacing={2}
            sx={{
              alignItems: 'stretch',
              position: 'relative',
              // Vertical divider between columns
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '1px',
                height: '100%',
                background: (theme) => theme.palette.mode === 'dark' ? 'linear-gradient(180deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.15) 100%)' : 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.03) 50%, rgba(0,0,0,0.1) 100%)',
                display: { xs: 'none', md: 'block' },
              },
            }}
          >
            {/* Column Headers */}
            <Grid item xs={12} md={6} sx={{ pr: { md: 2 } }}>
              <ColumnHeader mode="upfront" isActive={activeMode === 'upfront'} />
            </Grid>
            <Grid item xs={12} md={6} sx={{ pl: { md: 2 } }}>
              <ColumnHeader mode="threeYear" isActive={activeMode === 'threeYear'} />
            </Grid>

            {/* Total Annual Value */}
            <Grid item xs={12} md={6} sx={{ pr: { md: 2 } }}>
              <HeroValue mode="upfront" />
            </Grid>
            <Grid item xs={12} md={6} sx={{ pl: { md: 2 } }}>
              <HeroValue mode="threeYear" />
            </Grid>

            {/* VALUE BREAKDOWN - Title on both columns */}
            <Grid item xs={12} md={6} sx={{ pr: { md: 2 } }}>
              <SectionTitle color={colors.teal}>VALUE BREAKDOWN</SectionTitle>
              <ValueBreakdownSection />
            </Grid>
            <Grid item xs={12} md={6} sx={{ pl: { md: 2 } }}>
              <SectionTitle color={colors.teal}>VALUE BREAKDOWN</SectionTitle>
              <ValueBreakdownSection />
            </Grid>

            {/* CASH FLOW - Title on both columns */}
            <Grid item xs={12} md={6} sx={{ pr: { md: 2 } }}>
              <SectionTitle color={colors.orange}>CASH FLOW</SectionTitle>
              <CashFlowSection mode="upfront" />
            </Grid>
            <Grid item xs={12} md={6} sx={{ pl: { md: 2 } }}>
              <SectionTitle color={colors.orange}>CASH FLOW</SectionTitle>
              <CashFlowSection mode="threeYear" />
            </Grid>

            {/* 5-YEAR TOTAL - Title on both columns */}
            <Grid item xs={12} md={6} sx={{ pr: { md: 2 } }}>
              <SectionTitle color={colors.primary}>5-YEAR TOTAL</SectionTitle>
              <FiveYearSection mode="upfront" />
            </Grid>
            <Grid item xs={12} md={6} sx={{ pl: { md: 2 } }}>
              <SectionTitle color={colors.primary}>5-YEAR TOTAL</SectionTitle>
              <FiveYearSection mode="threeYear" />
            </Grid>
          </Grid>

          {/* CFO Perspective Table */}
          <Box
            sx={{
              mt: 3,
              p: 1.5,
              borderRadius: 1.5,
              background: (theme) => theme.palette.mode === 'dark' ? 'rgba(26, 31, 37, 0.6)' : 'rgba(248, 249, 250, 0.9)',
              border: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                mb: 1.5,
                display: 'block',
                fontSize: '0.7rem',
                letterSpacing: '0.03em',
              }}
            >
              CFO Perspective: Financial Decision Factors
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1.2fr 1fr 1fr',
                gap: 0,
                fontSize: '0.7rem',
              }}
            >
              {/* Header Row */}
              <Box sx={{ py: 0.5, px: 0.75, borderBottom: (theme) => `1px solid ${theme.palette.divider}`, color: 'text.secondary', fontSize: '0.65rem' }}>
                Factor
              </Box>
              <Box sx={{ py: 0.5, px: 0.75, borderBottom: (theme) => `1px solid ${theme.palette.divider}`, color: isDark ? colors.primary : 'text.primary', textAlign: 'center', fontWeight: 600, fontSize: '0.65rem' }}>
                Upfront
              </Box>
              <Box sx={{ py: 0.5, px: 0.75, borderBottom: (theme) => `1px solid ${theme.palette.divider}`, color: isDark ? colors.teal : 'text.primary', textAlign: 'center', fontWeight: 600, fontSize: '0.65rem' }}>
                3-Year Deferred
              </Box>

              {/* Rows */}
              {[
                { factor: 'Day 1 cash outlay', upfront: formatCurrency(upfront.totalCost), upfrontColor: colors.orange, threeYear: '$0', threeYearColor: colors.teal },
                { factor: 'Day 1 cash flow', upfront: 'Negative', upfrontColor: colors.orange, threeYear: 'POSITIVE', threeYearColor: colors.teal },
                { factor: 'Budget category', upfront: 'Often CapEx', upfrontColor: 'rgba(255,255,255,0.7)', threeYear: 'Often OpEx', threeYearColor: colors.teal },
                { factor: 'Tax treatment', upfront: 'May qualify for incentives', upfrontColor: colors.teal, threeYear: 'May expense immediately', threeYearColor: 'rgba(255,255,255,0.7)' },
                { factor: 'Approval level', upfront: 'Higher (capital)', upfrontColor: 'rgba(255,255,255,0.7)', threeYear: 'Lower (operating)', threeYearColor: colors.teal },
                { factor: 'Funding model', upfront: 'Capital expenditure', upfrontColor: 'rgba(255,255,255,0.7)', threeYear: 'Cash flow positive', threeYearColor: colors.teal },
                { factor: 'Payback period', upfront: `~${upfront.paybackMonths.toFixed(0)} months`, upfrontColor: 'rgba(255,255,255,0.7)', threeYear: 'NONE - always profitable', threeYearColor: colors.teal },
              ].map((row, idx) => (
                <Box key={idx} sx={{ display: 'contents' }}>
                  <Box sx={{ py: 0.5, px: 0.75, borderBottom: (theme) => `1px solid ${theme.palette.divider}`, color: 'text.secondary', fontSize: '0.65rem' }}>
                    {row.factor}
                  </Box>
                  <Box sx={{ py: 0.5, px: 0.75, borderBottom: (theme) => `1px solid ${theme.palette.divider}`, color: row.upfrontColor, textAlign: 'center', fontWeight: row.upfrontColor === colors.teal ? 600 : 400, fontSize: '0.65rem' }}>
                    {row.upfront}
                  </Box>
                  <Box sx={{ py: 0.5, px: 0.75, borderBottom: (theme) => `1px solid ${theme.palette.divider}`, color: row.threeYearColor, textAlign: 'center', fontWeight: row.threeYearColor === colors.teal ? 600 : 400, fontSize: '0.65rem' }}>
                    {row.threeYear}
                  </Box>
                </Box>
              ))}
            </Box>
            {/* Credit Approval Disclaimer */}
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 1,
                color: 'text.secondary',
                fontStyle: 'italic',
                textAlign: 'center',
                fontSize: '0.6rem'
              }}
            >
              3-year deferred payment available to qualified buyers, subject to credit approval.
            </Typography>
          </Box>

          {/* 5-Year Value Projection Charts - SIDE BY SIDE */}
          {projections && summary && (
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 1.5,
                  display: 'block',
                  fontSize: '0.7rem',
                  letterSpacing: '0.03em',
                }}
              >
                5-Year Value Projection Comparison
              </Typography>
              <Grid
                container
                spacing={2}
                sx={{
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '1px',
                    height: '100%',
                    background: (theme) => theme.palette.mode === 'dark' ? 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)' : 'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.02) 100%)',
                    display: { xs: 'none', md: 'block' },
                  },
                }}
              >
                {/* Upfront Chart */}
                <Grid item xs={12} md={6} sx={{ pr: { md: 2 } }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      background: (theme) => activeMode === 'upfront'
                        ? `${colors.primary}08`
                        : theme.palette.mode === 'dark' ? 'rgba(26, 31, 37, 0.4)' : 'rgba(248, 249, 250, 0.8)',
                      borderLeft: (theme) => activeMode === 'upfront'
                        ? `2px solid ${colors.primary}80`
                        : `2px solid ${theme.palette.divider}`,
                    }}
                  >
                    <SavingsProjectionChart
                      projections={projections}
                      treatmentCost={summary.total_treatment_cost}
                      paybackMonths={summary.weighted_average_payback_months}
                      annualSavings={summary.total_annual_savings}
                      totalAnnualValue={summary.total_annual_value}
                      paymentMode="upfront"
                      paymentMetrics={null}
                    />
                  </Box>
                </Grid>
                {/* 3-Year Chart */}
                <Grid item xs={12} md={6} sx={{ pl: { md: 2 } }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      background: (theme) => activeMode === 'threeYear'
                        ? `${colors.teal}08`
                        : theme.palette.mode === 'dark' ? 'rgba(26, 31, 37, 0.4)' : 'rgba(248, 249, 250, 0.8)',
                      borderLeft: (theme) => activeMode === 'threeYear'
                        ? `2px solid ${colors.teal}80`
                        : `2px solid ${theme.palette.divider}`,
                    }}
                  >
                    <SavingsProjectionChart
                      projections={projections}
                      treatmentCost={summary.total_treatment_cost}
                      paybackMonths={summary.weighted_average_payback_months}
                      annualSavings={summary.total_annual_savings}
                      totalAnnualValue={summary.total_annual_value}
                      paymentMode="threeYear"
                      paymentMetrics={threeYear}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* =============================================== */}
          {/* ADDITIONAL OUTPUTS - FEATURE PARITY WITH MAIN PAGE */}
          {/* =============================================== */}

          {/* 1. Electricity Price Escalation Control */}
          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: 1.5,
              background: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(255, 107, 53, 0.06)'
                : 'rgba(199, 70, 0, 0.08)',
              borderLeft: (theme) => `2px solid ${theme.palette.mode === 'dark'
                ? 'rgba(255, 107, 53, 0.4)'
                : 'rgba(199, 70, 0, 0.5)'}`,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <TrendingUpIcon sx={{ color: (theme) => theme.palette.mode === 'dark' ? `${colors.orange}99` : '#C74600', fontSize: '1rem' }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '0.7rem', letterSpacing: '0.03em' }}>
                Electricity Price Escalation
              </Typography>
              <Chip
                label={`${escalationRate.toFixed(1)}%/yr`}
                size="small"
                sx={{
                  ml: 'auto',
                  fontWeight: 600,
                  background: (theme) => theme.palette.mode === 'dark'
                    ? 'rgba(255, 107, 53, 0.15)'
                    : 'rgba(199, 70, 0, 0.18)',
                  color: (theme) => theme.palette.mode === 'dark' ? colors.orange : '#C74600',
                  fontSize: '0.6rem',
                  height: 20,
                }}
              />
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1.5, display: 'block', fontSize: '0.65rem' }}>
              Electricity prices typically increase 2-4% annually. Adjust to see impact on long-term savings.
            </Typography>
            <Box sx={{ px: 2 }}>
              <Slider
                value={escalationRate}
                onChange={(_, value) => setEscalationRate(value)}
                min={0}
                max={6}
                step={0.5}
                marks={[
                  { value: 0, label: '0%' },
                  { value: 2, label: '2%' },
                  { value: 3, label: '3%' },
                  { value: 4, label: '4%' },
                  { value: 6, label: '6%' },
                ]}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${value}%`}
                sx={{
                  color: 'warning.main',
                  '& .MuiSlider-thumb': {
                    '&:hover, &.Mui-focusVisible': {
                      boxShadow: (theme) => theme.palette.mode === 'dark'
                        ? '0 0 0 8px rgba(255, 107, 53, 0.16)'
                        : '0 0 0 8px rgba(199, 70, 0, 0.16)',
                    },
                  },
                  '& .MuiSlider-mark': {
                    backgroundColor: 'warning.light',
                  },
                  '& .MuiSlider-markLabel': {
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                  },
                }}
              />
            </Box>
          </Box>

          {/* 2. Year-by-Year Projection Table - SIDE BY SIDE */}
          {escalationData && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Upfront Table */}
              <Grid item xs={12} md={6} sx={{ pr: { md: 2 } }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    background: (theme) => activeMode === 'upfront'
                      ? `${colors.primary}08`
                      : theme.palette.mode === 'dark' ? 'rgba(26, 31, 37, 0.4)' : 'rgba(248, 249, 250, 0.8)',
                    borderLeft: (theme) => activeMode === 'upfront'
                      ? `2px solid ${colors.primary}80`
                      : `2px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: isDark ? colors.primary : 'text.primary',
                      mb: 1,
                      display: 'block',
                      fontSize: '0.7rem',
                      letterSpacing: '0.03em',
                    }}
                  >
                    UPFRONT PAYMENT - Year-by-Year
                  </Typography>
                  <YearlyProjectionTable
                    escalationData={escalationData}
                    electricityRate={beforeAfterData?.metadata?.electricityRate || 0.12}
                    paymentMode="upfront"
                    treatmentCost={summary.total_treatment_cost}
                  />
                </Box>
              </Grid>
              {/* 3-Year Table */}
              <Grid item xs={12} md={6} sx={{ pl: { md: 2 } }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    background: (theme) => activeMode === 'threeYear'
                      ? `${colors.teal}08`
                      : theme.palette.mode === 'dark' ? 'rgba(26, 31, 37, 0.4)' : 'rgba(248, 249, 250, 0.8)',
                    borderLeft: (theme) => activeMode === 'threeYear'
                      ? `2px solid ${colors.teal}80`
                      : `2px solid ${theme.palette.divider}`,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: isDark ? colors.teal : 'text.primary',
                      mb: 1,
                      display: 'block',
                      fontSize: '0.7rem',
                      letterSpacing: '0.03em',
                    }}
                  >
                    3-YEAR PAYMENT - Year-by-Year
                  </Typography>
                  <YearlyProjectionTable
                    escalationData={escalationData}
                    electricityRate={beforeAfterData?.metadata?.electricityRate || 0.12}
                    paymentMode="threeYear"
                    treatmentCost={summary.total_treatment_cost}
                  />
                </Box>
              </Grid>
            </Grid>
          )}

          {/* ============================================= */}
          {/* ENERGY - Before/After Treatment Impact        */}
          {/* ============================================= */}

          {/* 3. Before/After Treatment Chart */}
          {beforeAfterData && (
            <BeforeAfterChart beforeAfterData={beforeAfterData} />
          )}

          {/* ============================================= */}
          {/* IMPACT - ESG & Sustainability                 */}
          {/* ============================================= */}

          {/* ESG & Sustainability Section */}
          {cfoMetrics?.esg && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 1.5,
                background: (theme) => theme.palette.mode === 'dark'
                  ? 'rgba(76, 175, 80, 0.06)'
                  : 'rgba(46, 125, 50, 0.08)',
                borderLeft: (theme) => `2px solid ${theme.palette.mode === 'dark'
                  ? 'rgba(76, 175, 80, 0.4)'
                  : 'rgba(46, 125, 50, 0.5)'}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <NatureIcon sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#4CAF5099' : '#2E7D32', fontSize: '1rem' }} />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: '0.7rem',
                    letterSpacing: '0.03em',
                  }}
                >
                  ESG & Sustainability Impact
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.6rem', mb: 0.25 }}>
                      Carbon Reduction
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '1.25rem',
                        fontWeight: 700,
                        color: isDark ? colors.success : 'text.primary',
                        fontFamily: '"JetBrains Mono", monospace',
                        fontFeatureSettings: '"tnum"',
                      }}
                    >
                      {formatNumber(cfoMetrics.esg?.carbonReduction || environmental?.co2_avoided_tonnes || 0, 1)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>
                      tonnes CO2/yr
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.6rem', mb: 0.25 }}>
                      ESG Reporting Value
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '1.25rem',
                        fontWeight: 700,
                        color: isDark ? colors.success : 'text.primary',
                        fontFamily: '"JetBrains Mono", monospace',
                        fontFeatureSettings: '"tnum"',
                      }}
                    >
                      {formatNumber(cfoMetrics.esg?.treesEquivalent || 0, 0)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>
                      trees equivalent
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* ============================================= */}
          {/* EQUIPMENT - Breakdown and Analysis            */}
          {/* ============================================= */}

          {/* Energy & Treatment Metrics - 3 Card Row */}
          {summary && (
            <Grid container spacing={2} sx={{ mt: 3, mb: 3 }}>
              {/* Energy Saved Card */}
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    p: 2,
                    borderTop: `3px solid ${colors.success}`,
                    background: (theme) => theme.palette.mode === 'dark' ? 'rgba(26, 31, 37, 0.6)' : 'rgba(248, 249, 250, 0.9)',
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: 1.5,
                    height: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <BoltIcon sx={{ color: isDark ? colors.success : 'text.primary', fontSize: '1rem' }} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 500,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        fontSize: '0.6rem',
                      }}
                    >
                      Energy Saved
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      color: isDark ? colors.success : 'text.primary',
                      fontFamily: '"JetBrains Mono", monospace',
                      fontFeatureSettings: '"tnum"',
                    }}
                  >
                    {formatNumber(summary.total_kwh_saved || 0)}
                    <Typography component="span" sx={{ fontSize: '0.7rem', fontWeight: 500, color: 'text.secondary', ml: 0.5 }}>
                      kWh/year
                    </Typography>
                  </Typography>
                </Card>
              </Grid>

              {/* CO₂ Avoided Card */}
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    p: 2,
                    borderTop: `3px solid ${colors.success}`,
                    background: (theme) => theme.palette.mode === 'dark' ? 'rgba(26, 31, 37, 0.6)' : 'rgba(248, 249, 250, 0.9)',
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: 1.5,
                    height: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Co2Icon sx={{ color: isDark ? colors.success : 'text.primary', fontSize: '1rem' }} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 500,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        fontSize: '0.6rem',
                      }}
                    >
                      CO₂ Avoided
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      color: isDark ? colors.success : 'text.primary',
                      fontFamily: '"JetBrains Mono", monospace',
                      fontFeatureSettings: '"tnum"',
                    }}
                  >
                    {((summary.total_kwh_saved || 0) * 0.00042).toFixed(1)}
                    <Typography component="span" sx={{ fontSize: '0.7rem', fontWeight: 500, color: 'text.secondary', ml: 0.5 }}>
                      tonnes/year
                    </Typography>
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.55rem', display: 'block', mt: 0.5 }}>
                    = {Math.round(((summary.total_kwh_saved || 0) * 0.00042) / 4.6)} cars removed
                  </Typography>
                </Card>
              </Grid>

              {/* Treatment Cost Card */}
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    p: 2,
                    borderTop: `3px solid ${activeMode === 'threeYear' ? colors.success : colors.orange}`,
                    background: (theme) => theme.palette.mode === 'dark' ? 'rgba(26, 31, 37, 0.6)' : 'rgba(248, 249, 250, 0.9)',
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    borderRadius: 1.5,
                    height: '100%',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <AttachMoneyIcon sx={{ color: activeMode === 'threeYear' ? colors.success : colors.orange, fontSize: '1rem' }} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 500,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        fontSize: '0.6rem',
                      }}
                    >
                      {activeMode === 'threeYear' ? 'Year 1 Payment' : 'Treatment Cost'}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      color: activeMode === 'threeYear' ? colors.success : colors.orange,
                      fontFamily: '"JetBrains Mono", monospace',
                      fontFeatureSettings: '"tnum"',
                    }}
                  >
                    ${activeMode === 'threeYear'
                      ? Math.round((summary.total_treatment_cost || 0) / 3).toLocaleString()
                      : (summary.total_treatment_cost || 0).toLocaleString()}
                  </Typography>
                  {activeMode === 'threeYear' && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.55rem', display: 'block', mt: 0.5 }}>
                      of ${(summary.total_treatment_cost || 0).toLocaleString()} total (no interest)
                    </Typography>
                  )}
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Equipment Summary Stats - IMMEDIATELY before breakdown */}
          <Grid container spacing={2} sx={{ mt: 3, mb: 2 }}>
            <Grid item xs={12} md={6} sx={{ pr: { md: 2 } }}>
              <SectionTitle>EQUIPMENT SUMMARY</SectionTitle>
              <StatsGrid mode="upfront" />
            </Grid>
            <Grid item xs={12} md={6} sx={{ pl: { md: 2 } }}>
              <SectionTitle>EQUIPMENT SUMMARY</SectionTitle>
              <StatsGrid mode="threeYear" />
            </Grid>
          </Grid>

          {/* 4. Equipment Type Breakdown Cards */}
          {roiResults?.byType && (
            <EquipmentTypeBreakdown data={roiResults.byType} />
          )}

          {/* 5. Equipment ROI Comparison Chart */}
          {roiResults?.byType && (
            <Box sx={{ mt: 3 }}>
              <EquipmentComparisonChart byType={roiResults.byType} />
            </Box>
          )}

          {/* 6. Portfolio Summary - Integrated Equipment ROI Summary */}
          {roiResults?.portfolio && roiResults?.byType && (
            <Box sx={{ mt: 3 }}>
              <PortfolioSummary
                data={roiResults.portfolio}
                byTypeData={roiResults.byType}
                paymentMode={activeMode}
                paymentMetrics={paymentMetrics}
              />
            </Box>
          )}

          {/* 7. Payback Heatmap by Age */}
          {roiResults?.individual && (
            <PaybackHeatmap equipment={roiResults.individual} />
          )}

          {/* 8. CFO Recommendation Section */}
          {roiResults && cfoMetrics && companyConfig && (
            <CFORecommendationSection
              roiResults={roiResults}
              cfoMetrics={cfoMetrics}
              companyConfig={companyConfig}
            />
          )}

          {/* 9. Risk & Scale Analysis */}
          {roiResults && cfoMetrics && (
            <RiskScaleSection
              roiResults={roiResults}
              cfoMetrics={cfoMetrics}
            />
          )}

          {/* 10. Capital & Accounting Treatment */}
          {roiResults && paymentMetrics && (
            <CapitalAccountingSection
              roiResults={roiResults}
              paymentMetrics={paymentMetrics}
            />
          )}

          {/* Estimate Disclaimer */}
          <Box
            sx={{
              mt: 3,
              p: 2,
              borderRadius: 1.5,
              background: (theme) => theme.palette.mode === 'dark' ? 'rgba(26, 31, 37, 0.6)' : 'rgba(248, 249, 250, 0.9)',
              border: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                mb: 1,
                color: 'text.secondary',
                display: 'block',
                fontSize: '0.65rem',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
              }}
            >
              ESTIMATE DISCLAIMER
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, lineHeight: 1.6, display: 'block', fontSize: '0.6rem' }}>
              This assessment estimate is based on the <strong style={{ color: 'text.primary' }}>PermaFrost NMR corpus</strong> containing{' '}
              <strong style={{ color: 'text.primary' }}>174 case studies</strong> with <strong style={{ color: 'text.primary' }}>254 Professional Engineer-certified field measurements</strong>{' '}
              across <strong style={{ color: 'text.primary' }}>38 countries</strong>. Equipment-specific efficiency improvements are derived from corpus data:{' '}
              Chillers 21.1%, RTUs 22.3%, CRACs 26.1%, Split Systems 24.3%.
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1.5, lineHeight: 1.6, display: 'block', fontSize: '0.6rem' }}>
              Individual results reflect equipment-specific conditions. Values shown are based on validated corpus data
              from 116 PE-certified field studies with documented methodology.
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, color: isDark ? colors.primary : 'text.primary', fontSize: '0.6rem' }}>
              Contact: sales@powertronglobal.com | powertronglobal.com
            </Typography>
          </Box>

          {/* Key Message */}
          <Box
            sx={{
              mt: 3,
              p: 1.5,
              borderRadius: 1,
              background: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(78, 205, 196, 0.06)'
                : 'rgba(13, 115, 119, 0.08)',
              borderLeft: (theme) => `2px solid ${theme.palette.mode === 'dark'
                ? 'rgba(78, 205, 196, 0.4)'
                : 'rgba(13, 115, 119, 0.5)'}`,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
            }}
          >
            <CheckCircleIcon sx={{ color: (theme) => theme.palette.mode === 'dark' ? `${colors.teal}99` : '#0D7377', fontSize: '1rem', mt: 0.125 }} />
            <Box>
              <Typography variant="caption" sx={{ color: 'text.primary', fontWeight: 600, mb: 0.25, display: 'block', fontSize: '0.7rem' }}>
                Same 5-year outcome, different cash flow timing
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                Both options deliver identical total value and net profit over 5 years.
                Choose based on your organization's budget structure and cash flow preferences.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Metric Explanation Modal */}
      <MetricExplanationModal
        metric={openMetric}
        open={!!openMetric}
        onClose={() => setOpenMetric(null)}
        values={metricValues}
      />
    </>
  );
}

// Helper Components
function StatItem({ label, value, unit, highlight, highlightColor, metricKey, onInfoClick, subtext }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>
          {label}
        </Typography>
        {metricKey && onInfoClick && (
          <IconButton
            size="small"
            onClick={() => onInfoClick(metricKey)}
            sx={{ p: 0.25, color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)', '&:hover': { color: highlightColor || '#A0CE4E' } }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 10 }} />
          </IconButton>
        )}
      </Box>
      <Typography
        sx={{
          fontSize: '0.85rem',
          fontWeight: 700,
          color: highlight ? highlightColor : 'text.primary',
          fontFamily: '"JetBrains Mono", monospace',
          fontFeatureSettings: '"tnum"',
        }}
      >
        {value}
        {unit && (
          <Typography component="span" sx={{ fontSize: '0.6rem', color: 'text.secondary', ml: 0.5 }}>
            {unit}
          </Typography>
        )}
      </Typography>
      {subtext && (
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.5rem', display: 'block' }}>
          {subtext}
        </Typography>
      )}
    </Box>
  );
}

function MetricBox({ label, value, subtext, color, onInfoClick }) {
  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 1,
        background: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.05)',
        borderLeft: `2px solid ${color}60`,
        height: '100%',
        transition: 'all 0.15s ease',
        '&:hover': {
          background: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.08)',
          transform: 'translateX(2px)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6rem' }}>
          {label}
        </Typography>
        {onInfoClick && (
          <IconButton
            size="small"
            onClick={onInfoClick}
            sx={{ p: 0.25, color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)', '&:hover': { color } }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 11 }} />
          </IconButton>
        )}
      </Box>
      <Typography
        sx={{
          fontSize: '0.9rem',
          fontWeight: 700,
          color,
          fontFamily: '"JetBrains Mono", monospace',
          fontFeatureSettings: '"tnum"',
        }}
      >
        {value}
      </Typography>
      {subtext && (
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.55rem' }}>
          {subtext}
        </Typography>
      )}
    </Box>
  );
}

function CashFlowItem({ label, value, positive, negative, subtext, badge }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 0.5,
        px: 0.75,
        borderRadius: 0.5,
        background: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.04)',
        transition: 'all 0.15s ease',
        '&:hover': {
          background: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.08)',
          transform: 'translateX(2px)',
        },
      }}
    >
      <Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
          {label}
        </Typography>
        {subtext && (
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.55rem', display: 'block' }}>
            {subtext}
          </Typography>
        )}
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <Typography
          sx={{
            fontSize: '0.8rem',
            fontWeight: 700,
            color: (theme) => positive
              ? (theme.palette.mode === 'dark' ? '#4CAF50' : '#2E7D32')
              : negative
                ? (theme.palette.mode === 'dark' ? '#FF6B35' : '#C74600')
                : 'text.primary',
            fontFamily: '"JetBrains Mono", monospace',
            fontFeatureSettings: '"tnum"',
          }}
        >
          {value}
        </Typography>
        {badge && (
          <Chip
            label={badge}
            size="small"
            sx={{
              height: 14,
              fontSize: '0.5rem',
              fontWeight: 600,
              background: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(76, 175, 80, 0.15)'
                : 'rgba(46, 125, 50, 0.18)',
              color: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : '#2E7D32',
              border: 'none',
            }}
          />
        )}
      </Box>
    </Box>
  );
}
